import React from "react";
import PropTypes from "prop-types";
import { cn } from "../cn";
import { Skeleton } from "../Skeleton/Skeleton";
import styles from "./Stat.module.scss";

/**
 * StatGrid — responsive container for a row of `Stat` tiles.
 * Same auto-fit grid the legacy `.ops-stat-grid` used, so nothing reflows.
 */
export function StatGrid({ className, children, ...rest }) {
  return (
    <div className={cn(styles.grid, className)} {...rest}>
      {children}
    </div>
  );
}

/**
 * Stat / StatCard — a single metric tile (value + label, optional eyebrow/badge).
 * Replaces the hand-rolled `.ops-stat` divs.
 *
 * `loading` renders skeletons sized to the loaded value/label, so the tile keeps
 * its exact dimensions and nothing shifts when the number arrives. This is what
 * lets stat values that depend on a later data wave stay skeletons until ready
 * instead of popping in and re-shifting.
 *
 *   <Stat value="12" label="Assessments" />
 *   <Stat value={avg} label="Avg. progress" loading={statsLoading} />
 */
export function Stat({
  value,
  label,
  eyebrow,
  badge,
  loading = false,
  className,
  ...rest
}) {
  return (
    <div className={cn(styles.stat, className)} {...rest}>
      {eyebrow != null && <div className={styles.eyebrow}>{eyebrow}</div>}

      {loading ? (
        <Skeleton className={styles.valueSkeleton} />
      ) : (
        <div className={styles.value}>{value}</div>
      )}

      {loading ? (
        <Skeleton className={styles.labelSkeleton} />
      ) : (
        <div className={styles.label}>
          {label}
          {badge != null && <span className={styles.badge}>{badge}</span>}
        </div>
      )}
    </div>
  );
}

/** Alias: some call sites read more naturally as `StatCard`. */
export const StatCard = Stat;

const nodeType = PropTypes.node;
Stat.propTypes = {
  value: nodeType,
  label: nodeType,
  eyebrow: nodeType,
  badge: nodeType,
  loading: PropTypes.bool,
  className: PropTypes.string,
};
StatGrid.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Stat;
